import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ReactLenis } from "lenis/react";
import { Phone, Menu, X, KeyRound, MapPin } from "lucide-react";
import Marquee from "react-fast-marquee";
import { PHONE, PHONE_INTL, COMPANY, BRANDS, EMAIL, ADDRESSES, api } from "../lib/api";
import { GoogleRating } from "./Reviews";

const NAV = [
  { to: "/", label: "Accueil" },
  { to: "/services", label: "Services" },
  { to: "/villes", label: "Zones d'intervention" },
  { to: "/conseils", label: "Conseils" },
  { to: "/contact", label: "Devis gratuit" },
];

export const CallButton = ({ className = "", label, testid = "call-btn" }) => (
  <a href={`tel:${PHONE_INTL}`} data-testid={testid}
     className={`group inline-flex items-center gap-2.5 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-primary/25 transition-transform duration-200 hover:scale-105 ${className}`}>
    <Phone className="h-4 w-4" />
    <span>{label || PHONE}</span>
  </a>
);

export const PhoneBox = ({ className = "", testid = "phone-box" }) => (
  <div className={className}>
    <a href={`tel:${PHONE_INTL}`} data-testid={testid}
       className="group inline-flex items-center gap-3 rounded-2xl border-2 border-primary bg-primary/5 pl-5 pr-3 py-3 hover:bg-primary/10 transition-colors duration-200">
      <span className="text-left">
        <span className="block text-[10px] font-mono uppercase tracking-widest text-primary/70">Appeler maintenant · 7j/7 24h/24</span>
        <span className="block font-display font-semibold text-2xl tracking-tight text-zinc-900">{PHONE}</span>
      </span>
      <span className="grid place-items-center h-12 w-12 rounded-xl bg-primary shrink-0"><Phone className="h-5 w-5 text-white" /></span>
    </a>
    <div className="mt-3"><GoogleRating /></div>
  </div>
);

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => { const s = () => setScrolled(window.scrollY > 20); window.addEventListener("scroll", s); return () => window.removeEventListener("scroll", s); }, []);
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "glass border-b border-zinc-200 shadow-sm" : ""}`} data-testid="site-header">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
          <span className="grid place-items-center h-9 w-9 rounded-xl bg-primary shadow-sm"><KeyRound className="h-4 w-4 text-white" /></span>
          <span className="font-display font-semibold text-lg tracking-tight leading-none text-zinc-900">Serrurerie<span className="text-primary">77</span></span>
        </Link>
        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map(n => (
            <Link key={n.to} to={n.to} data-testid={`nav-${n.label}`}
              className={`text-sm link-underline transition-colors ${loc.pathname === n.to ? "text-zinc-900 font-medium" : "text-zinc-500 hover:text-zinc-900"}`}>{n.label}</Link>
          ))}
        </nav>
        <div className="hidden lg:block"><CallButton testid="header-call" /></div>
        <button className="lg:hidden text-zinc-900" onClick={() => setOpen(!open)} data-testid="menu-toggle" aria-label="Menu">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden glass border-t border-zinc-200 px-6 py-6 flex flex-col gap-5" data-testid="mobile-menu">
          {NAV.map(n => <Link key={n.to} to={n.to} className="text-zinc-700 text-lg">{n.label}</Link>)}
          <CallButton className="mt-2 justify-center" testid="mobile-call" />
        </div>
      )}
    </header>
  );
};

export const BrandMarquee = () => (
  <div className="py-8 border-y border-zinc-200 bg-zinc-50 relative z-10" data-testid="brand-marquee">
    <Marquee speed={30} gradient gradientColor="#fafafa" gradientWidth={120}>
      {BRANDS.concat(BRANDS).map((b, i) => (
        <span key={i} className="font-display text-2xl sm:text-3xl font-medium text-zinc-300 mx-16 tracking-tight">{b}</span>
      ))}
    </Marquee>
  </div>
);

const Footer = () => {
  const [cities, setCities] = useState([]);
  useEffect(() => { api.get("/cities").then(r => setCities(r.data)).catch(() => {}); }, []);
  return (
    <footer className="relative z-10 border-t border-zinc-200 bg-zinc-50" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
        <p className="overline mb-6">Un problème ? Appelez maintenant</p>
        <a href={`tel:${PHONE_INTL}`} className="block font-display font-semibold tracking-tighter text-zinc-900 leading-none text-5xl sm:text-7xl hover:text-primary transition-colors" data-testid="footer-phone">
          {PHONE}
        </a>
        <div className="mt-20 grid gap-12 md:grid-cols-4">
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <span className="grid place-items-center h-8 w-8 rounded-lg bg-primary"><KeyRound className="h-4 w-4 text-white" /></span>
              <span className="font-display font-semibold text-lg text-zinc-900">Serrurerie<span className="text-primary">77</span></span>
            </div>
            <p className="text-sm text-zinc-500 leading-relaxed">Dépannage serrurerie en urgence dans toute la Seine-et-Marne. Intervention 7j/7 — 24h/24.</p>
            <p className="text-sm text-zinc-500 mt-4">{EMAIL}</p>
            <div className="mt-5 space-y-2">
              {ADDRESSES.map((a, i) => (
                <p key={i} className="flex items-start gap-2 text-sm text-zinc-500"><MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" /><span>{a.street}, {a.cp} {a.city}</span></p>
              ))}
            </div>
          </div>
          <div>
            <p className="overline mb-4 !text-zinc-400">Services</p>
            <ul className="space-y-2.5 text-sm text-zinc-600">
              <li><Link to="/services/depannage-serrurerie" className="hover:text-primary">Dépannage urgence</Link></li>
              <li><Link to="/services/ouverture-de-porte" className="hover:text-primary">Ouverture de porte</Link></li>
              <li><Link to="/services/changement-de-serrure" className="hover:text-primary">Changement de serrure</Link></li>
              <li><Link to="/services/blindage-de-porte" className="hover:text-primary">Blindage de porte</Link></li>
              <li><Link to="/services" className="hover:text-primary">Tous les services →</Link></li>
            </ul>
          </div>
          <div className="md:col-span-2">
            <p className="overline mb-4 !text-zinc-400">Serrurier par ville · Seine-et-Marne</p>
            <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-zinc-500">
              {cities.map(c => <Link key={c.slug} to={`/serrurier/${c.slug}`} className="hover:text-primary">{c.name}</Link>)}
            </div>
          </div>
        </div>
        <div className="mt-16 pt-8 border-t border-zinc-200 flex flex-col sm:flex-row justify-between gap-4 text-xs text-zinc-400">
          <span>© {new Date().getFullYear()} {COMPANY}. Tous droits réservés.</span>
          <span>Intervention 7j/7 · 24h/24 · Devis gratuit</span>
        </div>
      </div>
    </footer>
  );
};

export const StickyCall = () => (
  <a href={`tel:${PHONE_INTL}`} data-testid="sticky-call"
     className="lg:hidden fixed bottom-5 inset-x-5 z-50 flex items-center justify-center gap-2.5 rounded-full bg-primary py-4 font-semibold text-white shadow-2xl pulse-ring">
    <Phone className="h-5 w-5" /> Appeler maintenant · {PHONE}
  </a>
);

export default function Layout({ children }) {
  return (
    <ReactLenis root options={{ lerp: 0.09, smoothWheel: true }}>
      <div className="App relative bg-white">
        <Header />
        <main className="relative z-10">{children}</main>
        <Footer />
        <StickyCall />
      </div>
    </ReactLenis>
  );
}
